<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';

interface TimeFormat {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const MILLISECONDS_THRESHOLD = 1000000000000;
const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_DAY = 24 * SECONDS_IN_HOUR;
const MAX_REASONABLE_DAYS = 365;
const MAX_REASONABLE_SECONDS = MAX_REASONABLE_DAYS * SECONDS_IN_DAY;
const UPDATE_INTERVAL_MS = 1000;

const props = defineProps<{
  minigame: TMiniGameTemplate;
}>();

const { playMiniGame } = useSmarticoMiniGames();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();

const currentUnixTimestamp = ref(getCurrentUnixTimestamp());
let timeUpdateInterval: NodeJS.Timeout | null = null;

function getCurrentUnixTimestamp(): number {
  return Math.floor(Date.now() / 1000);
}

function isTimestampInMilliseconds(timestamp: number): boolean {
  return timestamp > MILLISECONDS_THRESHOLD;
}

function convertMillisecondsToSeconds(timestamp: number): number {
  return Math.floor(timestamp / 1000);
}

function normalizeTimestamp(timestamp: number): number {
  return isTimestampInMilliseconds(timestamp)
    ? convertMillisecondsToSeconds(timestamp)
    : timestamp;
}

function isTimeRemainingReasonable(seconds: number): boolean {
  return seconds > 0 && seconds <= MAX_REASONABLE_SECONDS;
}

function calculateTimeRemaining(
  futureTimestamp: number,
  currentTimestamp: number,
): number {
  const normalizedFutureTime = normalizeTimestamp(futureTimestamp);
  const timeDifference = normalizedFutureTime - currentTimestamp;

  return Math.max(0, timeDifference);
}

function parseTimeIntoComponents(totalSeconds: number): TimeFormat {
  const days = Math.floor(totalSeconds / SECONDS_IN_DAY);
  const hours = Math.floor((totalSeconds % SECONDS_IN_DAY) / SECONDS_IN_HOUR);
  const minutes = Math.floor(
    (totalSeconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE,
  );

  const seconds = totalSeconds % SECONDS_IN_MINUTE;

  return { days, hours, minutes, seconds };
}

function formatTimeDisplay(timeComponents: TimeFormat): string {
  const { days, hours, minutes, seconds } = timeComponents;

  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  if (days > 0) {
    return `${days}d ${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  }

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

function formatRemainingTime(remainingSeconds: number): string {
  if (remainingSeconds <= 0) {
    return '';
  }

  if (!isTimeRemainingReasonable(remainingSeconds)) {
    return '';
  }

  const timeComponents = parseTimeIntoComponents(remainingSeconds);

  return formatTimeDisplay(timeComponents);
}

function canUserPlayMiniGame(): boolean {
  const { next_available_spin_ts } = props.minigame;

  if (!next_available_spin_ts) {
    return true;
  }

  const normalizedNextAvailableTime = normalizeTimestamp(
    next_available_spin_ts,
  );

  return currentUnixTimestamp.value >= normalizedNextAvailableTime;
}

function getRemainingWaitTime(): number {
  const { next_available_spin_ts } = props.minigame;

  if (!next_available_spin_ts) {
    return 0;
  }

  return calculateTimeRemaining(
    next_available_spin_ts,
    currentUnixTimestamp.value,
  );
}

const isPlayable = computed(() => canUserPlayMiniGame());

const remainingWaitTime = computed(() => getRemainingWaitTime());

const formattedCountdown = computed(() =>
  formatRemainingTime(remainingWaitTime.value),
);

async function handlePlayMiniGame(): Promise<void> {
  if (!isPlayable.value) {
    return;
  }

  if (!isAuthenticated.value) {
    openLogin();

    return;
  }

  await playMiniGame(props.minigame.id);
}

function startTimeUpdater(): void {
  timeUpdateInterval = setInterval(() => {
    currentUnixTimestamp.value = getCurrentUnixTimestamp();
  }, UPDATE_INTERVAL_MS);
}

function stopTimeUpdater(): void {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval);
    timeUpdateInterval = null;
  }
}

onMounted(startTimeUpdater);
onUnmounted(stopTimeUpdater);

const buyInCost = computed(() => {
  if (props.minigame.buyin_cost_points) {
    const value = props.minigame.buyin_cost_points;

    return {
      value,
      icon: 'currency-header/coin',
      name: value === 1 ? 'COIN' : 'COINS',
    };
  }
  if (props.minigame.buyin_cost_diamonds) {
    const value = props.minigame.buyin_cost_diamonds;

    return {
      value,
      icon: 'currency-header/diamond',
      name: value === 1 ? 'DIAMOND' : 'DIAMONDS',
    };
  }
  if (props.minigame.buyin_cost_gems) {
    const value = props.minigame.buyin_cost_gems;

    return {
      value,
      icon: 'currency-header/gem',
      name: value === 1 ? 'GEM' : 'GEMS',
    };
  }

  return null;
});

const isModalVisible = ref(false);

const formattedCustomData = computed(() => {
  if (props.minigame.custom_data && props.minigame.custom_data.length > 0) {
    return props.minigame.custom_data.split('\n');
  }

  return [];
});

const buttonText = computed(() => {
  switch (props.minigame.saw_game_type) {
    case 'giftbox':
      return 'ABRIR';

    case 'scratch':
      return 'RASPAR';

    case 'matchx':
      return 'PALPITAR';

    case 'quiz':
      return 'RESPONDER';

    case 'wheel':
      return 'GIRAR';

    case 'coin_flip':
      return 'JOGAR';

    default:
      return 'GIRAR';
  }
});

const buttonIcon = computed(() => {
  switch (props.minigame.saw_game_type) {
    case 'giftbox':
      return 'mini-games/abrir';

    case 'scratch':
      return 'mini-games/raspadinha-coin';

    case 'matchx':
      return 'messages';

    case 'quiz':
      return 'pen_square';

    case 'wheel':
      return 'mini-games/girar';

    case 'coin_flip':
      return 'mini-games/girar';

    default:
      return 'mini-games/girar';
  }
});

// Tipo de moeda para estilização
type CurrencyType = 'free' | 'diamonds' | 'gems' | 'coins';

const currencyType = computed<CurrencyType>(() => {
  if (props.minigame.buyin_cost_points && props.minigame.buyin_cost_points > 0)
    return 'coins';
  if (
    props.minigame.buyin_cost_diamonds &&
    props.minigame.buyin_cost_diamonds > 0
  )
    return 'diamonds';
  if (props.minigame.buyin_cost_gems && props.minigame.buyin_cost_gems > 0)
    return 'gems';

  return 'free';
});

// Cores e estilos por tipo de moeda (igual StoreCardV2.vue)
const currencyStyles = computed(() => {
  const styles: Record<
    CurrencyType,
    {
      cardBg: string;
      priceBg: string;
      priceText: string;
      borderGradient: string;
    }
  > = {
    free: {
      cardBg:
        'linear-gradient(180deg, rgba(1,6,18,0.40) 0%, rgba(11,31,79,0.40) 100%)',
      priceBg: '#262D41',
      priceText: 'white',
      borderGradient: 'linear-gradient(180deg, #131722 0%, #656565 100%)',
    },
    diamonds: {
      cardBg:
        'linear-gradient(180deg, rgba(2,10,31,0.46) 0%, rgba(8,44,133,0.46) 100%)',
      priceBg: '#00C9FF1A',
      priceText: '#00C9FF',
      borderGradient: 'linear-gradient(180deg, #1D2329 0%, #1BE0F8 100%)',
    },
    gems: {
      cardBg:
        'linear-gradient(180deg, rgba(0,43,11,0.20) 0%, rgba(0,255,64,0.20) 100%)',
      priceBg: '#00FF401A',
      priceText: '#81FFA1',
      borderGradient: 'linear-gradient(180deg, #1D2329 0%, #83F539 100%)',
    },
    coins: {
      cardBg:
        'linear-gradient(180deg, rgba(27,7,1,0.40) 0%, rgba(129,33,4,0.40) 100%)',
      priceBg: '#F66A0066',
      priceText: '#FF9B50',
      borderGradient: 'linear-gradient(180deg, #1D2329 0%, #F66A00 100%)',
    },
  };

  return styles[currencyType.value];
});

// Texto do preço formatado (valor e nome separados para layout)
const priceValue = computed(() => {
  if (buyInCost.value) {
    return buyInCost.value.value;
  }

  return null;
});

const priceName = computed(() => {
  if (buyInCost.value) {
    return buyInCost.value.name;
  }

  return 'GRATUITO';
});
</script>

<template>
  <!-- Wrapper externo para dar espaço para o timer (igual StoreCardV2) -->
  <div class="card-outer-wrapper">
    <!-- Timer Badge (fora do card para não ser cortado pelo overflow) -->
    <div
      v-if="props.minigame.next_available_spin_ts"
      class="timer-badge"
    >
      <NuxtIcon
        class="timer-icon"
        filled
        name="mini-games/clock"
      />
      <span class="timer-text">
        {{ formattedCountdown || '00:00:00' }}
      </span>
    </div>

    <!-- Card - Layout Figma -->
    <div
      class="minigame-card"
      :style="{
        background: currencyStyles.cardBg,
        '--border-gradient': currencyStyles.borderGradient,
      }"
    >
      <!-- Ícone de Termos e Condições -->
      <div
        v-if="props.minigame.custom_data"
        class="info-button"
        @click="isModalVisible = true"
      >
        <NuxtIcon
          class="text-[12px] text-white/50"
          name="info"
        />
      </div>

      <!-- Imagem -->
      <SmarticoApostatudoSmarticoImage
        :alt="props.minigame.name"
        class="card-image"
        height="96"
        object-fit="contain"
        :src="props.minigame.promo_image"
        width="96"
      />

      <!-- Título + Descrição (wrapper para centralizar) -->
      <div class="card-text-wrapper">
        <div class="card-title">
          {{ props.minigame.name }}
        </div>
        <div class="card-description">
          {{ props.minigame.promo_text }}
        </div>
      </div>

      <!-- Ações: Badge de Preço + Botão -->
      <div class="card-actions">
        <!-- Badge de Preço -->
        <div
          class="price-badge"
          :class="{ 'price-badge--free': !buyInCost }"
          :style="{ background: currencyStyles.priceBg }"
        >
          <NuxtIcon
            v-if="buyInCost"
            class="price-icon"
            filled
            :name="buyInCost.icon"
          />
          <div
            v-if="buyInCost"
            class="price-content"
          >
            <span
              class="price-value"
              :style="{ color: currencyStyles.priceText }"
            >
              {{ priceValue }}
            </span>
            <span
              class="price-name"
              :style="{ color: currencyStyles.priceText }"
            >
              {{ priceName }}
            </span>
          </div>
          <span
            v-else
            class="price-free-text"
            :style="{ color: currencyStyles.priceText }"
          >
            GRATÍS
          </span>
        </div>

        <!-- Botão Abrir -->
        <button
          v-if="isPlayable"
          class="open-button"
          @click="handlePlayMiniGame"
        >
          <div
            v-if="props.minigame.saw_game_type !== 'quiz'"
            class="w-3 h-3.5 flex items-center justify-center"
          >
            <NuxtIcon
              class="text-[14px] text-white"
              filled
              :name="buttonIcon"
            />
          </div>
          <span>{{ buttonText }} ({{ props.minigame.spin_count }})</span>
        </button>
        <button
          v-else
          class="open-button disabled"
          disabled
        >
          <NuxtIcon
            class="text-[12px] text-white"
            filled
            name="mini-games/locked"
          />
          <span>Limite</span>
        </button>
      </div>

      <!-- Ribbon (opcional) -->
      <div
        v-if="props.minigame.saw_template_ui_definition.background_sound"
        class="ribbon"
      >
        <span>{{
          props.minigame.saw_template_ui_definition.background_sound
        }}</span>
      </div>
    </div>
  </div>

  <BaseModalMissionModal v-model="isModalVisible">
    <template #header>
      <NuxtIcon
        class="text-xl text-white"
        name="mission/info"
      />
      <p class="text-lg font-bold text-white">Termos e condições</p>
    </template>
    <div v-if="props.minigame.custom_data">
      <p
        v-for="(line, index) in formattedCustomData"
        :key="index"
        class="mb-2 text-white/50 font-normal text-[13px]"
      >
        {{ line }}
      </p>
    </div>
  </BaseModalMissionModal>
</template>

<style scoped lang="postcss">
/* Card wrapper para dar espaço para o timer (igual StoreCardV2) */
.card-outer-wrapper {
  display: inline-block;
  padding-top: 12px;
  overflow: visible;
  position: relative;
}

.minigame-card {
  @apply flex flex-col items-center;
  @apply backdrop-blur-[10px] select-none;
  @apply relative overflow-hidden;
  /* Mobile: 200x217.26 conforme Figma */
  width: 200px;
  height: 217.26px;
  padding: 13.32px;
  border-radius: 13.32px;
  gap: 6px;
}

/* Desktop: 215px x 217.26px */
@media (min-width: 640px) {
  .minigame-card {
    width: 215px;
    height: 217.26px;
    padding: 13.32px;
    border-radius: 13.32px;
    gap: 6px;
  }
}

/* Borda com gradiente (igual StoreCardV2) */
.minigame-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  padding: 0.68px;
  background: var(--border-gradient);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: -1;
}

@media (min-width: 640px) {
  .minigame-card::before {
    padding: 0.9px;
  }
}

/* Timer Badge - posicionado no wrapper externo */
.timer-badge {
  @apply absolute rounded;
  left: 50%;
  top: 12px; /* alinhado com o topo do card */
  transform: translate(-50%, -50%);
  z-index: 10;
  height: 20px;
  padding: 0 6px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: #020617; /* slate-950 */
  outline: 1px solid #262626; /* neutral-800 */
  outline-offset: -1px;
}

.timer-icon {
  font-size: 12px;
  width: 12px;
  height: 12px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.timer-text {
  color: white;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 10.22px;
  font-weight: 800;
  line-height: 8.18px;
  white-space: nowrap;
}

.card-image {
  @apply flex-shrink-0;
  /* Mobile e Desktop: 96px conforme Figma */
  width: 96px;
  height: 96px;
}

.card-text-wrapper {
  @apply flex flex-col self-stretch justify-center;
  flex: 1;
  min-height: 0;
  gap: 2px;
}

.card-title {
  @apply self-stretch text-white font-bold;
  /* Mobile e Desktop: 16px conforme Figma */
  font-size: 16px;
  line-height: 16px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  overflow: hidden;
  flex-shrink: 0;
  min-height: 16px;
}

.card-description {
  @apply self-stretch text-white/50 font-medium;
  /* Mobile e Desktop: 14px conforme Figma */
  font-size: 14px;
  line-height: 16px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  min-height: 32px; /* garante espaço para 2 linhas */
}

.card-actions {
  @apply self-stretch inline-flex justify-center items-stretch;
  /* Mobile e Desktop: gap 6px, altura 36px conforme Figma */
  gap: 6px;
  height: 36px;
}

.price-badge {
  @apply rounded-lg flex justify-center items-center;
  flex: 0.7;
  @apply backdrop-blur-[10px];
  /* Mobile e Desktop conforme Figma */
  padding: 6px 10px;
  gap: 5px;
  min-width: 0;
}

.price-icon {
  /* Mobile e Desktop */
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.price-content {
  @apply inline-flex flex-col justify-center items-start;
  gap: 1px;
}

.price-value {
  @apply text-left;
  font-weight: 700;
  /* Mobile e Desktop */
  font-size: 14px;
  line-height: 14px;
}

.price-name {
  @apply text-left uppercase;
  font-weight: 800;
  /* Mobile e Desktop */
  font-size: 9px;
  line-height: 9px;
  letter-spacing: 0.02em;
}

.price-badge--free {
  @apply justify-center;
}

.price-free-text {
  @apply uppercase text-center;
  font-weight: 800;
  /* Mobile e Desktop conforme Figma */
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 0.02em;
}

.open-button {
  @apply rounded flex justify-center items-center;
  flex: 1.5;
  @apply text-white font-extrabold;
  @apply hover:brightness-90 transition cursor-pointer overflow-hidden;
  background: radial-gradient(
    ellipse 138% 168.3% at 36.76% -16.67%,
    #ff704a 0%,
    #ff3400 100%
  );
  outline: 0.68px solid rgba(255, 255, 255, 0.5);
  outline-offset: -0.68px;
  /* Mobile e Desktop conforme Figma */
  padding: 6px;
  gap: 4.77px;
  border-radius: 4px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  line-height: 16px;
  min-width: 0;
  white-space: nowrap;
}

.open-button span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.open-button.disabled {
  @apply opacity-60 cursor-not-allowed;
  background: rgba(115, 115, 115, 0.4);
  outline-color: #525252;
}

.ribbon {
  @apply absolute w-[140px] flex items-center justify-center p-1;
  @apply bg-[#f97316] z-10;
  top: 20px;
  right: -40px;
  transform: rotate(45deg);
}

.ribbon span {
  @apply text-white text-[9px] font-extrabold whitespace-nowrap overflow-hidden text-ellipsis;
}

.info-button {
  @apply absolute top-2 left-2 w-5 h-5;
  @apply flex items-center justify-center;
  @apply bg-white/5 rounded-full cursor-pointer;
  @apply hover:bg-white/10 transition;
  z-index: 5;
}
</style>
